import React from 'react';
import { TrendingUp, TrendingDown, Minus, Shield } from 'lucide-react';

const TREND_CONFIG = {
  improving: {
    icon: TrendingUp,
    color: 'text-green-500',
  },
  declining: {
    icon: TrendingDown,
    color: 'text-red-500',
  },
  stable: {
    icon: Minus,
    color: 'text-yellow-500',
  },
};

const getAdherenceColor = (rate) => {
  if (rate >= 80) return 'text-green-500';
  if (rate >= 60) return 'text-yellow-500';
  return 'text-red-500';
};

const getStrokeColor = (rate) => {
  if (rate >= 80) return 'stroke-green-500';
  if (rate >= 60) return 'stroke-yellow-500';
  return 'stroke-red-500';
};

const PlanAdherence = ({ data = {} }) => {
  const {
    adherence_rate = 0,
    total_tagged = 0,
    trend = 'stable',
    trend_message = '',
    best_symbol = null,
  } = data;

  const hasData = total_tagged > 0;
  const TrendIcon = TREND_CONFIG[trend]?.icon || Minus;
  const trendColor = TREND_CONFIG[trend]?.color || 'text-muted-foreground';

  // SVG circle progress
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (Math.min(adherence_rate, 100) / 100) * circumference;

  return (
    <div className="bg-card text-card-foreground rounded-lg shadow-sm border border-border p-4 md:p-6">
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-foreground">Plan Adherence</h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          How well you follow your trading rules
        </p>
      </div>

      {!hasData ? (
        <div className="flex items-center justify-center h-32">
          <p className="text-sm text-muted-foreground">
            No plan adherence data yet.
          </p>
        </div>
      ) : (
        <div className="flex flex-col items-center">
          {/* Circular Progress */}
          <div className="relative w-24 h-24 mb-3">
            <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
              <circle
                cx="50"
                cy="50"
                r={radius}
                fill="none"
                className="stroke-muted"
                strokeWidth="6"
              />
              <circle
                cx="50"
                cy="50"
                r={radius}
                fill="none"
                className={getStrokeColor(adherence_rate)}
                strokeWidth="6"
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                style={{ transition: 'stroke-dashoffset 0.5s ease' }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className={`text-xl font-bold ${getAdherenceColor(adherence_rate)}`}>
                {adherence_rate}%
              </span>
            </div>
          </div>

          <p className="text-xs font-medium text-foreground mb-1">Plan Adherence Rate</p>

          {/* Trend */}
          <div className={`flex items-center gap-1 mb-3 ${trendColor}`}>
            <TrendIcon className="w-3 h-3" />
            <span className="text-[10px]">{trend.charAt(0).toUpperCase() + trend.slice(1)}</span>
          </div>

          {/* Trend Message */}
          {trend_message && (
            <p className="text-xs text-muted-foreground text-center mb-2 leading-relaxed">
              {trend_message}
            </p>
          )}

          {/* Best Symbol */}
          {best_symbol && (
            <div className="flex items-center gap-1.5 mt-1 px-2 py-1 rounded bg-muted/50">
              <Shield className="w-3 h-3 text-green-500" />
              <span className="text-[10px] text-muted-foreground">
                Best on <span className="font-medium text-foreground">{best_symbol.symbol}</span>
                {' '}({best_symbol.adherence_rate}% across {best_symbol.trade_count} trades)
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PlanAdherence;
